/**
 * 🪢 **THE LEAD (TAUMUR) AND THE TETHER PEG (TJÓÐURHÆLL) — D128, 2026-09-29.**
 *
 * 🎯 **haugur chose every rule from one table:**
 *
 * | | His choice |
 * |---|---|
 * | 🪢 Recipe | **3 `thradur` → 1 `taumur`**, and **2 `prik` → 1 `tjodurhaell`** |
 * | 🐑 On | hold a taumur, **right button** on a sheep — one lead per sheep, several at once |
 * | 🚶 Follow | it walks to you past **3 m** and **snaps past 12 m** |
 * | 🔒 Whose | leading makes it **yours**, exactly as carrying does |
 * | 🪵 Peg | right-click it while leading and **every** led sheep is tied, within **5 m** |
 * | ♾️ Wear | **none** |
 *
 * 📖 **Minecraft** (minecraft.wiki, *Lead*): snaps at 12 blocks, a tied mob stays
 * within 5 blocks of the fence, leads never wear out. 🔴 **We have no fences**,
 * and Icelandic farms tethered animals in the home field — a rope (**tjóður**)
 * to a peg in the ground (**tjóðurhæll**).
 *
 * ## 🔴 ONE IDEA IN THE MIND: A HOME CIRCLE
 *
 * A led sheep's home is **the person leading it, radius 3 m**; a tied sheep's
 * is **the peg, radius 5 m**. Outside it, {@link led} or {@link tethered} walks
 * it back; inside it, `chooseDestination` picks wander spots **inside the
 * circle**, never outside and then clamped — the fish's lesson.
 *
 * ⚠️ **A lead is not in the sheep's memory**, because memory holds numbers and a
 * lead names a person. The server keeps who leads whom and hands the brain a
 * {@link Home} each beat, the way `leader` is handed over. **A tether IS in
 * memory** (`tetherX/Y/Z`, the peg's cell) because it outlives everybody and is
 * written to the database.
 */

import type { Behaviour, Creature, Sense, Vec3 } from './creature.ts';

/** 🪢 The lead. */
export const LEAD_ITEM = 'taumur';
/** 🪵 The tether peg — the item and the block share the key. */
export const PEG_BLOCK = 'tjodurhaell';

/** 🚶 **A led sheep walks to you past this many metres.** A dial. */
export const LEAD_SLACK = 3;
/** 💥 **Past this the rope snaps**, metres — Minecraft's 12 blocks. */
export const LEAD_SNAP = 12;
/** ⭕ **A tied sheep keeps within this of its peg**, metres — Minecraft's 5. */
export const TETHER_RADIUS = 5;
/** 🐑 **How far a lead reaches to be put on**, metres — the knife's 3. */
export const LEAD_REACH = 3;

/** Which kinds take a lead. ⚠️ A table: nothing is leadable by omission. */
const LEADABLE: ReadonlySet<string> = new Set(['kind']);

/** Can this kind be led at all? */
export function isLeadable(kind: string): boolean {
  return LEADABLE.has(kind);
}

/** ⭕ Where a creature must stay, and why. Handed to the brain each beat. */
export interface Home {
  readonly at: Vec3;
  readonly radius: number;
  readonly kind: 'lead' | 'peg';
}

/** 🪵 **The peg this creature is tied to, as a cell — or `undefined`.** */
export function tetherOf(self: Creature): Vec3 | undefined {
  const x = self.memory.tetherX;
  const y = self.memory.tetherY;
  const z = self.memory.tetherZ;
  if (x === undefined || y === undefined || z === undefined) return undefined;
  return { x, y, z };
}

/** 🪵 Tie it to the peg in this cell. */
export function tieTo(self: Creature, cell: Vec3): void {
  self.memory.tetherX = cell.x;
  self.memory.tetherY = cell.y;
  self.memory.tetherZ = cell.z;
}

/** 🪵 Untie it. */
export function untie(self: Creature): void {
  delete self.memory.tetherX;
  delete self.memory.tetherY;
  delete self.memory.tetherZ;
}

/** 🪵 Is it tied to the peg in exactly this cell? */
export function isTiedTo(self: Creature, cell: Vec3): boolean {
  const t = tetherOf(self);
  return t !== undefined && t.x === cell.x && t.y === cell.y && t.z === cell.z;
}

/**
 * ⭕ **Its home circle from a peg** — the middle of the peg's cell, at the
 * cell's floor. `undefined` when it is not tied.
 */
export function pegHome(self: Creature): Home | undefined {
  const t = tetherOf(self);
  if (t === undefined) return undefined;
  return { at: { x: t.x + 0.5, y: t.y, z: t.z + 0.5 }, radius: TETHER_RADIUS, kind: 'peg' };
}

/** 💥 Has the rope snapped — is the sheep further than {@link LEAD_SNAP} from its holder? */
export function leadSnaps(sheep: Vec3, holder: Vec3): boolean {
  return Math.hypot(sheep.x - holder.x, sheep.z - holder.z) > LEAD_SNAP;
}

function flatDistance(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/**
 * 🚶 **Back inside the circle** — the shared body of {@link led} and
 * {@link tethered}.
 *
 * 🔴 **The goal is a point INSIDE the circle, on the line from home to the
 * sheep**, so it stops a pace inside rather than walking into the person or
 * the peg. ⚠️ **Hurries when well outside** — the table's *"hurries when it
 * falls behind"*.
 */
function homeward(name: string, kind: Home['kind'], priority: number): Behaviour {
  return {
    name,
    priority,
    flags: ['move'],
    wants: (sense: Sense) => {
      const home = sense.home;
      return (
        home !== undefined &&
        home !== null &&
        home.kind === kind &&
        flatDistance(sense.self.at, home.at) > home.radius
      );
    },
    act(sense: Sense) {
      const home = sense.home;
      if (home === undefined || home === null) return {};
      const d = flatDistance(sense.self.at, home.at);
      const stop = home.radius * 0.6;
      const k = d > 0 ? stop / d : 0;
      const goal = {
        x: home.at.x + (sense.self.at.x - home.at.x) * k,
        y: sense.self.at.y,
        z: home.at.z + (sense.self.at.z - home.at.z) * k,
      };
      return { goal, pace: d > home.radius * 2 ? 2.4 : 1.3 };
    },
  };
}

/** 🪢 **Led** — walk back to whoever holds the lead. Above everything but panic. */
export function led(priority: number): Behaviour {
  return homeward('Led', 'lead', priority);
}

/** 🪵 **Tethered** — walk back inside the peg's circle. */
export function tethered(priority: number): Behaviour {
  return homeward('Tethered', 'peg', priority);
}
